// src/pages/PrivacyPolicyPage.jsx
import React from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const PrivacyPolicyPage = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <Box sx={{ py: 8, minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Paper 
          elevation={3}
          sx={{
            p: 4,
            background: isDarkMode 
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'none',
            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`
          }}
        >
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, color: '#5980a6' }}>
            Privacy Policy
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
            Last updated: {new Date().toLocaleDateString()}
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              1. Information We Collect
            </Typography>
            <Typography variant="body1" paragraph>
              We collect information you provide directly to us, such as when you create an account, subscribe to our service, or contact us for support.
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              Personal Information:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Name and email address" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Payment information (processed securely through third-party processors)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Communication preferences" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Support inquiries and correspondence" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
              Usage Information:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Login times and frequency" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Features accessed and usage patterns" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Device and browser information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="IP address and general location data" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              2. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the information we collect to:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Provide and maintain our service" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Process payments and manage subscriptions" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Send trading signals and market analysis" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Provide customer support" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Improve our service and develop new features" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Send important account and service updates" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Comply with legal obligations" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              3. Information Sharing and Disclosure
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell, trade, or otherwise transfer your personal information to outside parties except as described below:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Service Providers"
                  secondary="We may share information with trusted third-party service providers who assist us in operating our service (e.g., payment processors, email services)"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Legal Requirements"
                  secondary="We may disclose information when required by law, regulation, or court order"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Business Transfers"
                  secondary="In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction"
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              4. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Encryption of data in transit and at rest" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Regular security assessments and updates" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Access controls and authentication requirements" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Secure payment processing through certified providers" />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              5. Cookies and Tracking Technologies
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies and similar tracking technologies to enhance your experience on our service. These technologies help us:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Remember your login status and preferences" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Analyze usage patterns to improve our service" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Provide personalized content and features" />
              </ListItem>
            </List>
            <Typography variant="body1" paragraph>
              You can control cookie settings through your browser preferences.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              6. Your Rights and Choices
            </Typography>
            <Typography variant="body1" paragraph>
              You have the following rights regarding your personal information:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Access and Update"
                  secondary="You can access and update your account information through your profile settings"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Data Portability"
                  secondary="You can request a copy of your personal data in a machine-readable format"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Deletion"
                  secondary="You can request deletion of your account and associated data"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Communication Preferences"
                  secondary="You can opt out of non-essential communications while maintaining your account"
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              7. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We retain your personal information for as long as necessary to provide our service and fulfill the purposes outlined in this policy. When you cancel your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain certain information for legal or regulatory purposes.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              8. International Data Transfers
            </Typography>
            <Typography variant="body1" paragraph>
              Your information may be processed and stored in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with this privacy policy and applicable data protection laws.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              9. Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date. Your continued use of our service after such changes constitutes acceptance of the updated policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              10. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions about this privacy policy or our data practices, please contact us at:
              <br />
              Email: support@XAUPilot.com
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;